import React, {useEffect, useState} from 'react'
import {useIntl} from "react-intl"
import * as dayjs from 'dayjs'
import {LOCALES} from "../i18n/locales";
import {isNullOrUndefined} from "./ReactUtils";
import {onlyDigits} from "./Utils";
import {useSolv} from "./SolvProvider";

export const defaultIntlParams = {}

export function useIntlEx() {

    const fromNowRegex = new RegExp("(an?|[0-9]+) (second|minute|hour|day|week|month|quarter|year)(s)?")

    const intl = useIntl()

    const msg = (id, params) => {
      params = {...defaultIntlParams, ...params}
      let m
      try {
        m = intl.formatMessage({id: id}, params)
        return m
      }
      catch (e) {
        return id
      }
    }

    const exists = (id) => {
      const m = intl.formatMessage({id: id})
      return (m !== id)
    }

    intl.msg = msg
    intl.exists = exists

    const intFormat = new Intl.NumberFormat(intl.locale,{ minimumFractionDigits: 0, maximumFractionDigits: 0 })
    const intCompactFormat = new Intl.NumberFormat(intl.locale,{ minimumFractionDigits: 0, maximumFractionDigits: 2, notation: "compact" })
    const numFormat = new Intl.NumberFormat(intl.locale,{ minimumFractionDigits: 2, maximumFractionDigits: 2 })
    const numFormat2 = (decimals = 2) => new Intl.NumberFormat(intl.locale,{ minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    const numCompactFormat = new Intl.NumberFormat(intl.locale,{ minimumFractionDigits: 2, maximumFractionDigits: 2, notation: "compact" })
    const numExtraCompactFormat = new Intl.NumberFormat(intl.locale,{ minimumFractionDigits: 1, maximumFractionDigits: 1, notation: "compact" })
    const numCurrencyFormat = (ccy) => new Intl.NumberFormat(intl.locale,{ style: "currency", currency: ccy, currencyDisplay: "code", minimumFractionDigits: 2, maximumFractionDigits: 2 })
    const numAccountingFormat = {
      format: function (n) {
        n = parseFloat(n)
        if (n < 0) {
          return `(${numFormat.format(Math.abs(n))})`
        }
        else {
          return numFormat.format(n)
        }
      }
    }
    const numSignedFormat = {
      format: function (n) {
        n = parseFloat(n)
        const v = numFormat.format(n)
        return n > 0 ? `+${v}` : v
      }
    }

    const longDateFormat = new Intl.DateTimeFormat(intl.locale, { dateStyle: 'long' })
    const mediumDateFormat = new Intl.DateTimeFormat(intl.locale, { dateStyle: 'medium' })
    const shortDateFormat = new Intl.DateTimeFormat(intl.locale, { dateStyle: 'short' })
    const isoDateFormat = {
      format: function (dateTime) {
        let d
        if (dateTime.getMonth) {
          d = dateTime
        }
        else {
          d = new Date(dateTime)
        }
        return  `${d.getUTCFullYear()}-${(d.getUTCMonth() + 1).toString().padStart(2, '0')}-${d.getUTCDate().toString().padStart(2, '0')}`
      }
    }
    const isoCompactDateFormat = {
      format: function (dateTime) {
        let d
        if (dateTime.getMonth) {
          d = dateTime
        }
        else {
          d = new Date(dateTime)
        }
        return  `${d.getUTCFullYear()}${(d.getUTCMonth() + 1).toString().padStart(2, '0')}${d.getUTCDate().toString().padStart(2, '0')}`
      }
    }
    const longTimeFormat = new Intl.DateTimeFormat(intl.locale, { timeStyle: 'long' })
    const mediumTimeFormat = new Intl.DateTimeFormat(intl.locale, { timeStyle: 'medium' })
    const shortTimeFormat = new Intl.DateTimeFormat(intl.locale, { timeStyle: 'short' })
    const isoTimeFormat = {
      format: function (dateTime) {
        let d
        if (dateTime.getHours) {
          d = dateTime
        }
        else {
          d = new Date(dateTime)
        }
        return  `${d.getUTCHours().toString().padStart(2, '0')}:${(d.getUTCMinutes() + 1).toString().padStart(2, '0')}:${d.getUTCSeconds().toString().padStart(2, '0')}`
      }
    }
    const isoCompactTimeFormat = {
      format: function (dateTime) {
        let d
        if (dateTime.getHours) {
          d = dateTime
        }
        else {
          d = new Date(dateTime)
        }
        return  `${d.getUTCHours().toString().padStart(2, '0')}${(d.getUTCMinutes() + 1).toString().padStart(2, '0')}${d.getUTCSeconds().toString().padStart(2, '0')}`
      }
    }
    const longDateTimeFormat = new Intl.DateTimeFormat(intl.locale, { dateStyle: 'long', timeStyle: 'long' })
    const mediumDateTimeFormat = new Intl.DateTimeFormat(intl.locale, { dateStyle: 'medium', timeStyle: 'medium' })
    const shortDateTimeFormat = new Intl.DateTimeFormat(intl.locale, { dateStyle: 'short', timeStyle: 'short' })
    const isoDateTimeFormat = {
      format: function (dateTime) {
        return isoDateFormat.format(dateTime) + "T" + isoTimeFormat.format(dateTime)
      }
    }
    const isoCompactDateTimeFormat = {
      format: function (dateTime) {
        return isoCompactDateFormat.format(dateTime) + "T" + isoCompactTimeFormat.format(dateTime)
      }
    }
    const relativeTimeFormatter = new Intl.RelativeTimeFormat(intl.locale, {numeric: 'auto'})

    const DIVISIONS = [
      { amount: 60, name: 'seconds' },
      { amount: 60, name: 'minutes' },
      { amount: 24, name: 'hours' },
      { amount: 7, name: 'days' },
      { amount: 4.34524, name: 'weeks' },
      { amount: 12, name: 'months' },
      { amount: Number.POSITIVE_INFINITY, name: 'years' }
    ]

    intl.regionDisplayNames = new Intl.DisplayNames([intl.locale], {type: "region"})

    intl.regionDisplayName = function (regionId) {
      if (regionId) {
        try {
          return intl.regionDisplayNames.of(regionId)
        }
        catch (err) {
          return ""
        }
      }
      else {
        return ""
      }
    }

    intl.date = function (dateTime, format) {
      if (dateTime) {
        let fmt
        switch (format) {
          case "short":
          case "SHORT":
            fmt = shortDateFormat
            break
          case "medium":
          case "MEDIUM":
            fmt = mediumDateFormat
            break
          case "long":
          case "LONG":
            fmt = longDateFormat
            break
          case "iso":
          case "ISO":
            fmt = isoDateFormat
            break
          case "iso-compact":
          case "ISO-COMPACT":
            fmt = isoCompactDateFormat
            break
          default:
            fmt = mediumDateFormat
            break
        }
        return fmt.format(dateTime)
      }
      else {
        return ""
      }
    }

    intl.time = function (dateTime, format) {
      let fmt
      switch (format) {
        case "short":
        case "SHORT":
          fmt = shortTimeFormat
          break
        case "long":
        case "LONG":
          fmt = longTimeFormat
          break
        case "iso":
        case "ISO":
          fmt = isoTimeFormat
          break
        case "iso-compact":
        case "ISO-COMPACT":
          fmt = isoCompactTimeFormat
          break
        default:
          fmt = mediumTimeFormat
          break
      }
      return fmt.format(dateTime)
    }

    intl.dateTime = function (dateTime, format) {
        let fmt
        switch (format) {
          case "short":
          case "SHORT":
            fmt = shortDateTimeFormat
            break
          case "long":
          case "LONG":
            fmt = longDateTimeFormat
            break
          case "iso":
          case "ISO":
            fmt = isoDateTimeFormat
            break
          case "iso-compact":
          case "ISO-COMPACT":
            fmt = isoCompactDateTimeFormat
            break
          default:
            fmt = mediumDateTimeFormat
            break
        }
        return fmt.format(dateTime)
    }

    intl.fmFromNow = function(dateTime) {
        const dt = dayjs(dateTime)
        let value = ""
        let unit = "second"
        const fromNow = dt.fromNow().trim()
        if (fromNow === "a few seconds ago") {
            const s = dayjs().diff(dt, "second")
            value = s
            if (s > 1) {
                unit = unit + "s"
            }
        }
        const r = fromNow.match(fromNowRegex)
        if (r && r.length > 1) {
            value = r[1]
            if (value === "a" || value === "an") {
                value = "1"
            }
            unit = r[2]
            if (r[3]) {
                unit += "s"
            }
        }
        return intl.msg("time_from_now", {value: value, unit: intl.msg(`time_unit_${unit}`)})
    }

    intl.fmToNow = function(dateTime) {
        const dt = dayjs(dateTime)
        let value = ""
        let unit = "second"
        const toNow = dt.toNow(true).trim()
        if (toNow === "a few seconds") {
            const s = dt.diff(dayjs(), "second")
            value = s
            if (s > 1) {
                unit = unit + "s"
            }
        }
        const r = toNow.match(fromNowRegex)
        if (r && r.length > 1) {
            value = r[1]
            if (value === "a" || value === "an") {
                value = "1"
            }
            unit = r[2]
            if (r[3]) {
                unit += "s"
            }
        }
        return intl.msg("time_to_now", {value: value, unit: intl.msg(`time_unit_${unit}`)})
    }

    intl.fmNowRel = function(dateTime) {
      if (dayjs(dateTime).isAfter(dayjs())) {
        return intl.fmToNow(dateTime)
      }
      else {
        return intl.fmFromNow(dateTime)
      }
    }

    intl.fmDaysFrom = function(dateTime) {
        const dt = dayjs(dateTime)
        return intFormat.format(dt.diff(dayjs(), "day"))
    }

    intl.int = function (num, format) {
      let fmt = intFormat
      switch (format) {
        case "compact":
        case "COMPACT":
          fmt = intCompactFormat
          break
        default:
          fmt = intFormat
          break
      }
      return fmt.format(num)
    }

    intl.num = function (num, format, opt) {
      if (isNullOrUndefined(num) || Number.isNaN(num)) {
        return null
      }
      let fmt = numFormat
      switch (format) {
        case "compact":
        case "COMPACT":
          fmt = numCompactFormat
          break
        case "extracompact":
        case "extraCOMPACT":
          fmt = numExtraCompactFormat
          break
        case "currency":
        case "CURRENCY":
          fmt = numCurrencyFormat(opt?.currencyCode || "USD")
          break
        case "accounting":
        case "ACCOUNTING":
          fmt = numAccountingFormat
          break
        case "SIGNED":
          fmt = numSignedFormat
          break
        default:
          fmt = numFormat2(opt?.decimals || 2)
          break
      }
      return fmt.format(num)
    }

    intl.Fm = function(props) {
        return (
            props.text ? (
                intl.formatMessage({id: props.text}, props.params)
            ) : props.int ? (
                intFormat.format(props.int)
            ) : props.num ? (
                intFormat.format(props.num)
            ) : props.dateTime ? (
                intl.formatDateTime(props.dateTime)
            ) : props.fromNow ? (
                intl.formatFromNow(props.dateTime)
            ) : (
                <></>
            )
        )
    }

    intl.numFormat = numFormat
    intl.intFormat = intFormat

    return { intl }
}

const langOpts = Object.keys(LOCALES).map(l => {
  const engName = LOCALES[l].engName
  return {
    value: l,
    label: (LOCALES[l].langCode === "en") ? `${LOCALES[l].localName}` : `${LOCALES[l].localName} (${engName})`,
    engName: engName
  }
})

langOpts.sort((a, b) => a.engName.localeCompare(b.engName))

export function getAllLangOpts() {
  return langOpts
}

export function getLangOpt(lang) {
  return langOpts.find(i => i.value === lang)
}

export function useLangOpts() {

  const langOpts = Object.keys(LOCALES).map(l => {
    const engName = LOCALES[l].engName
    return {
      value: l,
      label: (LOCALES[l].langCode === "en") ? `${LOCALES[l].localName}` : `${LOCALES[l].localName} (${engName})`,
      engName: engName
    }
  })

  langOpts.sort((a, b) => a.engName.localeCompare(b.engName))

  function findLangOpt(v) {
    return langOpts.find(i => i.value === v)
  }

  return (
    {
      langOpts,
      findLangOpt
    }
  )

}

export const countriesCallingCodes = {"US": "1", "CA": "1", "EG": "20", "SS": "211", "EH": "212", "MA": "212", "DZ": "213", "TN": "216", "LY": "218", "GM": "220", "SN": "221", "MR": "222", "ML": "223", "GN": "224", "CI": "225", "BF": "226", "NE": "227", "TG": "228", "BJ": "229", "MU": "230", "LR": "231", "SL": "232", "GH": "233", "NG": "234", "TD": "235", "CF": "236", "CM": "237", "CV": "238", "ST": "239", "GQ": "240", "GA": "241", "CG": "242", "CD": "243", "AO": "244", "GW": "245", "IO": "246", "SC": "248", "SD": "249", "RW": "250", "ET": "251", "SO": "252", "DJ": "253", "KE": "254", "TZ": "255", "UG": "256", "BI": "257", "MZ": "258", "ZM": "260", "MG": "261", "RE": "262", "YT": "262", "ZW": "263", "NA": "264", "MW": "265", "LS": "266", "BW": "267", "SZ": "268", "KM": "269", "ZA": "27", "SH": "290", "ER": "291", "AW": "297", "FO": "298", "GL": "299", "GR": "30", "NL": "31", "BE": "32", "FR": "33", "ES": "34", "GI": "350", "PT": "351", "LU": "352", "IE": "353", "IS": "354", "AL": "355", "MT": "356", "CY": "357", "FI": "358", "BG": "359", "HU": "36", "LT": "370", "LV": "371", "EE": "372", "MD": "373", "AM": "374", "BY": "375", "AD": "376", "MC": "377", "SM": "378", "VA": "379", "UA": "380", "RS": "381", "ME": "382", "HR": "385", "SI": "386", "BA": "387", "MK": "389", "IT": "39", "RO": "40", "CH": "41", "CZ": "420", "SK": "421", "LI": "423", "AT": "43", "GB": "44", "DK": "45", "SE": "46", "NO": "47", "SJ": "47", "PL": "48", "DE": "49", "FK": "500", "BZ": "501", "GT": "502", "SV": "503", "HN": "504", "NI": "505", "CR": "506", "PA": "507", "PM": "508", "HT": "509", "PE": "51", "MX": "52", "CU": "53", "AR": "54", "BR": "55", "CL": "56", "CO": "57", "VE": "58", "MF": "590", "BL": "590", "BO": "591", "GY": "592", "EC": "593", "PY": "595", "SR": "597", "UY": "598", "CW": "599", "MY": "60", "CC": "61", "AU": "61", "CX": "61", "ID": "62", "PH": "63", "NZ": "64", "PN": "64", "SG": "65", "TH": "66", "TL": "670", "AQ": "672", "BN": "673", "NR": "674", "PG": "675", "TO": "676", "SB": "677", "VU": "678", "FJ": "679", "PW": "680", "WF": "681", "CK": "682", "NU": "683", "WS": "685", "KI": "686", "NC": "687", "TV": "688", "PF": "689", "TK": "690", "FM": "691", "MH": "692", "RU": "7", "KZ": "7", "JP": "81", "KR": "82", "VN": "84", "KP": "850", "HK": "852", "MO": "853", "KH": "855", "LA": "856", "CN": "86", "BD": "880", "TW": "886", "TR": "90", "IN": "91", "PK": "92", "AF": "93", "LK": "94", "MM": "95", "MV": "960", "LB": "961", "JO": "962", "SY": "963", "IQ": "964", "KW": "965", "SA": "966", "YE": "967", "OM": "968", "PS": "970", "AE": "971", "IL": "972", "BH": "973", "QA": "974", "BT": "975", "MN": "976", "NP": "977", "IR": "98", "TJ": "992", "TM": "993", "AZ": "994", "GE": "995", "KG": "996", "UZ": "998"}

export function useCallingCode(props) {

  const {intl} = useIntlEx()

  const countriesCallingCodes = {"US": "1", "CA": "1", "EG": "20", "SS": "211", "EH": "212", "MA": "212", "DZ": "213", "TN": "216", "LY": "218", "GM": "220", "SN": "221", "MR": "222", "ML": "223", "GN": "224", "CI": "225", "BF": "226", "NE": "227", "TG": "228", "BJ": "229", "MU": "230", "LR": "231", "SL": "232", "GH": "233", "NG": "234", "TD": "235", "CF": "236", "CM": "237", "CV": "238", "ST": "239", "GQ": "240", "GA": "241", "CG": "242", "CD": "243", "AO": "244", "GW": "245", "IO": "246", "SC": "248", "SD": "249", "RW": "250", "ET": "251", "SO": "252", "DJ": "253", "KE": "254", "TZ": "255", "UG": "256", "BI": "257", "MZ": "258", "ZM": "260", "MG": "261", "RE": "262", "YT": "262", "ZW": "263", "NA": "264", "MW": "265", "LS": "266", "BW": "267", "SZ": "268", "KM": "269", "ZA": "27", "SH": "290", "ER": "291", "AW": "297", "FO": "298", "GL": "299", "GR": "30", "NL": "31", "BE": "32", "FR": "33", "ES": "34", "GI": "350", "PT": "351", "LU": "352", "IE": "353", "IS": "354", "AL": "355", "MT": "356", "CY": "357", "FI": "358", "BG": "359", "HU": "36", "LT": "370", "LV": "371", "EE": "372", "MD": "373", "AM": "374", "BY": "375", "AD": "376", "MC": "377", "SM": "378", "VA": "379", "UA": "380", "RS": "381", "ME": "382", "HR": "385", "SI": "386", "BA": "387", "MK": "389", "IT": "39", "RO": "40", "CH": "41", "CZ": "420", "SK": "421", "LI": "423", "AT": "43", "GB": "44", "DK": "45", "SE": "46", "NO": "47", "SJ": "47", "PL": "48", "DE": "49", "FK": "500", "BZ": "501", "GT": "502", "SV": "503", "HN": "504", "NI": "505", "CR": "506", "PA": "507", "PM": "508", "HT": "509", "PE": "51", "MX": "52", "CU": "53", "AR": "54", "BR": "55", "CL": "56", "CO": "57", "VE": "58", "MF": "590", "BL": "590", "BO": "591", "GY": "592", "EC": "593", "PY": "595", "SR": "597", "UY": "598", "CW": "599", "MY": "60", "CC": "61", "AU": "61", "CX": "61", "ID": "62", "PH": "63", "NZ": "64", "PN": "64", "SG": "65", "TH": "66", "TL": "670", "AQ": "672", "BN": "673", "NR": "674", "PG": "675", "TO": "676", "SB": "677", "VU": "678", "FJ": "679", "PW": "680", "WF": "681", "CK": "682", "NU": "683", "WS": "685", "KI": "686", "NC": "687", "TV": "688", "PF": "689", "TK": "690", "FM": "691", "MH": "692", "RU": "7", "KZ": "7", "JP": "81", "KR": "82", "VN": "84", "KP": "850", "HK": "852", "MO": "853", "KH": "855", "LA": "856", "CN": "86", "BD": "880", "TW": "886", "TR": "90", "IN": "91", "PK": "92", "AF": "93", "LK": "94", "MM": "95", "MV": "960", "LB": "961", "JO": "962", "SY": "963", "IQ": "964", "KW": "965", "SA": "966", "YE": "967", "OM": "968", "PS": "970", "AE": "971", "IL": "972", "BH": "973", "QA": "974", "BT": "975", "MN": "976", "NP": "977", "IR": "98", "TJ": "992", "TM": "993", "AZ": "994", "GE": "995", "KG": "996", "UZ": "998"}
  const flagUrl = (countryCode) => `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`;
  const callingCodes = Object.entries(countriesCallingCodes)
    .map(([countryCode, callingCode]) => ({
      callingCode,
      countryCode,
      icon: flagUrl(countryCode),
    }))
    .sort((a, b) => Number(a.callingCode) - Number(b.callingCode) || a.countryCode.localeCompare(b.countryCode));

  return ({
    callingCodes,
    findCallingCode: (callingCode) => callingCodes.find(c => c.callingCode === onlyDigits(callingCode)) || callingCodes[0],
    findCallingCodeByCountryCode: (countryCode) => callingCodes.find(c => c.countryCode === countryCode) || callingCodes[0]
  })

}


export function determineLocale(locale) {

  let l = locale?.toLowerCase()?.trim() || "auto"

  if (["auto", "system"].includes(l)) {
    const lang = navigator.languages ? navigator.languages[0] : (navigator.language || navigator.userLanguage)
    const lang_parts = lang.split("-")
    if (lang_parts.length > 1) {
      if (lang_parts[0] === "en") {
        if (lang_parts[1] === "CA") {
          l = "en"
        }
        else if (lang_parts[1] === "GB") {
          l = "en"
        }
        else if (lang_parts[1] === "US") {
          l = "en"
        }
        else {
          l = "en"
        }
      }
      else if (lang_parts[0] === "zh") {
        if (lang_parts[1] === "CN") {
          l = "zh-CN"
        }
        else if (lang_parts[1] === "HK") {
          l = "zh-TW"
        }
        else {
          l = "zh-CN"
        }
        if (l === "zh-CN") {
          l = "zh-HANS"
        }
        else {
          l = "zh-HANT"
        }
      }
      // // log.debug("Set detected lang=", tlocale)
    }
    else {
      l = lang
    }

    if (!l) {
      l = "en"
    }
  }

  if (!(LOCALES.hasOwnProperty(l))) {
    l = "en"
  }

  return l

}

export const flagEmoji = (iso) =>
  String(iso || "")
    .toUpperCase()
    .replace(/[A-Z]/g, (ch) => String.fromCodePoint(0x1f1e6 + ch.charCodeAt(0) - 65));


export function useMessages({locale}) {

  const {env} = useSolv()

  const [state, setState] = useState({locale: null, messages: null})

  useEffect(() => {

    if (!locale || !env.msgUrl) return
    let cancelled = false

    const load = async (loc) => {
      const res = await fetch(`${env.msgUrl}/${locale}.json`)
      if (!res.ok) throw new Error(`Failed to load messages for ${loc}`)
      return res.json()
    }

    (async () => {
      try {
        // Always load English as a fallback so missing keys don't show raw ids
        const [fallback, localized] = await Promise.all([
          load(LOCALES.en),
          locale === LOCALES.en ? Promise.resolve({}) : load(locale),
        ])
        if (!cancelled) setState({locale, messages: {...fallback, ...localized}})
      } catch (e) {
        console.error(e)
        // Optionally: set English-only messages here so the app still renders
      }
    })()

    return () => { cancelled = true }
  }, [locale, env.msgUrl])

  // Only report messages once they match the requested locale
  return state.locale === locale ? state.messages : null
}

